import React, { useState, useCallback } from "react";
import {
  Card,
  Text,
  Group,
  Stack,
  ActionIcon,
  Menu,
  Badge,
  Modal,
  Button,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconTrash, IconFlask, IconDotsVertical } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import DeviceStatusBadge from "./DeviceStatusBadge";
import { bridgeService } from "../../services/bridgeService";
import type { Workstation } from "../../services/bridgeService";

dayjs.extend(relativeTime);

interface Props {
  workstation: Workstation;
}

const WorkstationStatusCard: React.FC<Props> = ({ workstation }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => bridgeService.deleteWorkstation(workstation.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: "Workstation Deleted",
        message: `"${workstation.name}" has been removed.`,
        color: "green",
      });
      setConfirmOpen(false);
    },
    onError: (err: any) => {
      notifications.show({
        title: "Delete Failed",
        message: err?.response?.data?.message || "Failed to delete workstation",
        color: "red",
      });
    },
  });

  const stainerMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      bridgeService.setStainerBridge(workstation.id, enabled),
    onSuccess: (_res, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: enabled ? "Staining Hub Set" : "Staining Hub Cleared",
        message: enabled
          ? `"${workstation.name}" now handles all staining.`
          : `"${workstation.name}" is no longer the Staining Hub.`,
        color: "green",
      });
    },
    onError: (err: any) => {
      notifications.show({
        title: "Update Failed",
        message:
          err?.response?.data?.message || "Failed to update Staining Hub",
        color: "red",
      });
    },
  });

  const handleCardClick = useCallback(() => {
    navigate(`/admin/bridge/${workstation.id}`);
  }, [navigate, workstation.id]);

  const handleOpenDelete = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleDelete = useCallback(() => {
    deleteMutation.mutate();
  }, [deleteMutation]);

  const handleToggleStainer = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      stainerMutation.mutate(!workstation.isStainerBridge);
    },
    [stainerMutation, workstation.isStainerBridge]
  );

  const handleMenuClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <>
      <Card
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
        style={{ cursor: "pointer" }}
        onClick={handleCardClick}
      >
        <Group justify="space-between" wrap="nowrap" mb="xs">
          <Text fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
            {workstation.name}
          </Text>
          <Menu position="bottom-end" offset={4} withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleMenuClick}
                aria-label="Workstation actions"
                style={{ flexShrink: 0 }}
              >
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconFlask size={16} />}
                disabled={stainerMutation.isPending}
                onClick={handleToggleStainer}
              >
                {workstation.isStainerBridge
                  ? "Unset as Staining Hub"
                  : "Set as Staining Hub"}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={handleOpenDelete}
              >
                Delete workstation
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Group gap="xs" mb="md">
          <DeviceStatusBadge status={workstation.status} />
          {workstation.isStainerBridge && (
            <Badge
              color="teal"
              variant="light"
              leftSection={<IconFlask size={12} />}
            >
              Staining Hub
            </Badge>
          )}
        </Group>
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Devices: {workstation.onlineDeviceCount}/{workstation.deviceCount}{" "}
            online
          </Text>
          <Text size="sm" c="dimmed">
            Last seen:{" "}
            {workstation.lastSeenAt
              ? dayjs(workstation.lastSeenAt).fromNow()
              : "Never"}
          </Text>
        </Stack>
      </Card>

      <Modal
        opened={confirmOpen}
        onClose={handleCloseDelete}
        title="Confirm Delete Workstation"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete workstation &quot;
            {workstation.name}
            &quot;? This will also delete all associated devices, sessions, and
            commands. This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseDelete}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default WorkstationStatusCard;
